import type { WorkflowDefinition } from "@/stores/workflowsStore";

export interface DBTableReference {
  /** The table name referenced in the SQL. */
  tableName: string;
  /** Column names hinted at by INSERT/UPDATE statements (best-effort). */
  columnHints: string[];
  /** Node IDs that reference this table. */
  nodeIds: string[];
}

/**
 * Extract table names referenced by db-query nodes in a workflow definition.
 *
 * Parses SQL statements to find table names from:
 * - SELECT ... FROM table
 * - INSERT INTO table
 * - UPDATE table SET ...
 * - DELETE FROM table
 * - JOIN table
 *
 * Also extracts column hints from INSERT INTO table (col1, col2, ...) patterns.
 *
 * When `propValues` is provided, `{{$props.X}}` placeholders in SQL strings are
 * resolved before parsing so that parameterized table names (common in templates)
 * are correctly detected.
 */
export function extractDBQueryTables(
  definition: WorkflowDefinition | undefined,
  propValues?: Record<string, unknown>,
): DBTableReference[] {
  if (!definition?.nodes) return [];

  const tableMap = new Map<string, { originalName: string; columnHints: Set<string>; nodeIds: Set<string> }>();

  for (const [nodeId, node] of Object.entries(definition.nodes)) {
    if (node.type !== "db-query") continue;

    const rawSql = node.payload?.sql;
    if (typeof rawSql !== "string" || !rawSql.trim()) continue;

    const sql = propValues ? resolvePropsInString(rawSql, propValues) : rawSql;

    const tables = extractTablesFromSQL(sql);
    for (const { tableName, columns } of tables) {
      const normalized = tableName.toLowerCase();
      if (!tableMap.has(normalized)) {
        tableMap.set(normalized, { originalName: tableName, columnHints: new Set(), nodeIds: new Set() });
      }
      const entry = tableMap.get(normalized)!;
      entry.nodeIds.add(nodeId);
      for (const col of columns) {
        entry.columnHints.add(col);
      }
    }
  }

  return Array.from(tableMap.values()).map(({ originalName, columnHints, nodeIds }) => ({
    tableName: originalName,
    columnHints: Array.from(columnHints),
    nodeIds: Array.from(nodeIds),
  }));
}

function resolvePropsInString(str: string, propValues: Record<string, unknown>): string {
  return str.replace(/\{\{\$props\.(\w+)\}\}/g, (match, key) => {
    const val = propValues[key];
    return val !== undefined && val !== null && val !== "" ? String(val) : match;
  });
}

interface ParsedTable {
  tableName: string;
  columns: string[];
}

/**
 * Simple SQL parser that extracts table names and column hints.
 * Not a full SQL parser — handles common DML patterns.
 *
 * Known limitations:
 * - Subquery table names are included (benign: they'll show as "Ready" if they exist)
 * - CTE aliases are excluded via a pre-pass, but deeply nested CTEs may not be caught
 */
function extractTablesFromSQL(sql: string): ParsedTable[] {
  const results: ParsedTable[] = [];
  const seen = new Set<string>();

  // Normalize whitespace for easier matching
  const normalized = sql.replace(/\s+/g, " ").trim();

  // Pre-pass: collect CTE aliases (WITH name AS (...)) so we can exclude them from results.
  const cteAliases = new Set<string>();
  const cteMatches = normalized.matchAll(/\bWITH\s+(?:RECURSIVE\s+)?(\w+)\s+AS\s*\(/gi);
  for (const m of cteMatches) {
    cteAliases.add(m[1].toLowerCase());
  }
  // Also handle chained CTEs: WITH a AS (...), b AS (...)
  const chainedCteMatches = normalized.matchAll(/,\s*(\w+)\s+AS\s*\(/gi);
  for (const m of chainedCteMatches) {
    cteAliases.add(m[1].toLowerCase());
  }

  // Match: INSERT [OR IGNORE|REPLACE] INTO / REPLACE INTO "table" (col1, col2, ...)
  const insertWithColsMatches = normalized.matchAll(
    /(?:INSERT\s+(?:OR\s+\w+\s+)?|REPLACE\s+)INTO\s+(?:"([^"]+)"|(\w+))\s*\(([^)]+)\)/gi,
  );
  const insertedTables = new Set<string>();
  for (const insertMatch of insertWithColsMatches) {
    const tableName = insertMatch[1] || insertMatch[2];
    const columns = insertMatch[3]
      .split(",")
      .map(c => c.trim().replace(/^"|"$/g, ""))
      .filter(c => c && /^[a-zA-Z_]\w*$/.test(c));
    const key = tableName.toLowerCase();
    insertedTables.add(key);
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ tableName, columns });
    }
  }

  // Match: INSERT [OR IGNORE|REPLACE] INTO / REPLACE INTO table without column list
  const insertSimpleMatches = normalized.matchAll(
    /(?:INSERT\s+(?:OR\s+\w+\s+)?|REPLACE\s+)INTO\s+(?:"([^"]+)"|(\w+))/gi,
  );
  for (const insertSimple of insertSimpleMatches) {
    const tableName = insertSimple[1] || insertSimple[2];
    const key = tableName.toLowerCase();
    if (!seen.has(key) && !insertedTables.has(key)) {
      seen.add(key);
      results.push({ tableName, columns: [] });
    }
  }

  // Match: SELECT ... FROM "table" or FROM table
  // Skip function calls like jsonb_array_elements_text(...) by checking for trailing '('
  const fromMatches = normalized.matchAll(/\bFROM\s+(?:"([^"]+)"|(\w+))\s*(\(?)/gi);
  for (const match of fromMatches) {
    const quoted = match[1];
    const unquoted = match[2];
    const trailingParen = match[3];
    if (!quoted && trailingParen === "(") continue;
    const tableName = quoted || unquoted;
    const key = tableName.toLowerCase();
    if (!seen.has(key) && (quoted || !isReservedWord(unquoted)) && !cteAliases.has(key)) {
      seen.add(key);
      results.push({ tableName, columns: [] });
    }
  }

  // Match: JOIN "table" or JOIN table
  // Skip function calls like LATERAL JOIN func(...) by checking for trailing '('
  const joinMatches = normalized.matchAll(/\bJOIN\s+(?:"([^"]+)"|(\w+))\s*(\(?)/gi);
  for (const match of joinMatches) {
    const quoted = match[1];
    const unquoted = match[2];
    const trailingParen = match[3];
    if (!quoted && trailingParen === "(") continue;
    const tableName = quoted || unquoted;
    const key = tableName.toLowerCase();
    if (!seen.has(key) && (quoted || !isReservedWord(unquoted)) && !cteAliases.has(key)) {
      seen.add(key);
      results.push({ tableName, columns: [] });
    }
  }

  // Match: UPDATE "table" SET or UPDATE table SET
  const updateMatches = normalized.matchAll(/UPDATE\s+(?:"([^"]+)"|(\w+))\s+SET\s+/gi);
  for (const updateMatch of updateMatches) {
    const tableName = updateMatch[1] || updateMatch[2];
    const key = tableName.toLowerCase();

    // Extract column names from SET clause, skipping content inside parentheses
    // to avoid picking up subquery column names.
    const columns: string[] = [];
    const afterSet = normalized.slice(updateMatch.index! + updateMatch[0].length);
    // Walk the string tracking paren depth; split on commas at depth 0; stop at top-level WHERE.
    const assignments: string[] = [];
    let depth = 0;
    let current = "";
    for (let i = 0; i < afterSet.length; i++) {
      const ch = afterSet[i];
      if (ch === "(") {
        depth++;
        current += ch;
      } else if (ch === ")") {
        depth--;
        current += ch;
      } else if (depth === 0 && ch === ",") {
        assignments.push(current);
        current = "";
      } else if (depth === 0 && /^\s*WHERE\b/i.test(afterSet.slice(i))) {
        break;
      } else {
        current += ch;
      }
    }
    if (current.trim()) assignments.push(current);

    for (const a of assignments) {
      const colMatch = a.trim().match(/^(?:"([^"]+)"|(\w+))\s*=/);
      if (colMatch) {
        const col = colMatch[1] || colMatch[2];
        if (/^[a-zA-Z_]\w*$/.test(col)) columns.push(col);
      }
    }

    if (!seen.has(key) && (updateMatch[1] || !isReservedWord(tableName)) && !cteAliases.has(key)) {
      seen.add(key);
      results.push({ tableName, columns });
    }
  }

  // Match: DELETE FROM "table" or DELETE FROM table
  const deleteMatches = normalized.matchAll(/DELETE\s+FROM\s+(?:"([^"]+)"|(\w+))/gi);
  for (const deleteMatch of deleteMatches) {
    const tableName = deleteMatch[1] || deleteMatch[2];
    const key = tableName.toLowerCase();
    if (!seen.has(key) && (deleteMatch[1] || !isReservedWord(tableName)) && !cteAliases.has(key)) {
      seen.add(key);
      results.push({ tableName, columns: [] });
    }
  }

  return results;
}

const SQL_RESERVED = new Set([
  "select",
  "from",
  "where",
  "insert",
  "into",
  "update",
  "delete",
  "set",
  "values",
  "join",
  "inner",
  "outer",
  "left",
  "right",
  "on",
  "and",
  "or",
  "not",
  "in",
  "is",
  "null",
  "like",
  "between",
  "exists",
  "having",
  "group",
  "order",
  "by",
  "asc",
  "desc",
  "limit",
  "offset",
  "as",
  "distinct",
  "count",
  "sum",
  "avg",
  "min",
  "max",
  "case",
  "when",
  "then",
  "else",
  "end",
  "create",
  "table",
  "alter",
  "drop",
  "index",
  "primary",
  "key",
  "foreign",
  "references",
  "default",
  "constraint",
  "union",
  "all",
  "true",
  "false",
  "replace",
  // Table-valued functions (not real tables)
  "json_each",
  "json_tree",
  "generate_series",
  "pragma_table_info",
  // Postgres set-returning / table-valued functions
  "jsonb_array_elements_text",
  "jsonb_array_elements",
  "jsonb_each",
  "jsonb_each_text",
  "unnest",
]);

function isReservedWord(word: string): boolean {
  return SQL_RESERVED.has(word.toLowerCase());
}
